/**
 * Ce fel de fișier poate fi un document, și sub ce nume ajunge pe disc.
 *
 * ⚠️ **Regulile astea au fost scrise de trei ori înainte să ajungă aici.** Filtrul de
 * atașamente din `@pcrm/be/email` avea tabelul tip→extensie, controllerul de documente își
 * scrisese o copie pentru numele sugerat la descărcare, iar ecranul de încărcare ar fi
 * adus a patra — în `accept`-ul lui `<input type="file">`. Un tip nou primit (azi `.heic`,
 * mâine altceva) trebuia adăugat în toate, iar cel uitat nu dădea eroare: pur și simplu
 * ieșea un fișier `.bin` sau un buton care refuza o poză bună.
 *
 * `scope:shared`, deci **fără `node:`**: aceleași funcții rulează și în browser, unde ecranul
 * de încărcare verifică fișierul ales înainte să-l trimită. Hash-ul, care are nevoie de
 * `node:crypto`, stă dincolo — vezi `hashContent` din `@pcrm/be/documents`.
 *
 * Funcții pure, fără NestJS: partea cea mai ușor de greșit din tot lanțul de fișiere e calea,
 * iar ea trebuie testabilă fără disc.
 */

/** Tipurile primite: un act e un PDF sau o poză a unei hârtii. */
export const ACCEPTED_DOCUMENT_MIME_TYPES: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'image/webp',
  'image/tiff',
];

/** Extensiile acceptate, pentru mesajele care trimit totul ca `application/octet-stream`. */
export const ACCEPTED_DOCUMENT_EXTENSIONS: readonly string[] = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.heif',
  '.webp',
  '.tif',
  '.tiff',
];

/**
 * Peste atât, nu e un act: e un film sau o arhivă. Limita obișnuită a unui atașament.
 *
 * Aceeași valoare pentru ce vine pe email și pentru ce se încarcă de mână, dinadins: două
 * praguri ar fi însemnat că același contract scanat intră pe un drum și e refuzat pe
 * celălalt, fără ca cineva să poată spune de ce.
 */
export const MAX_DOCUMENT_FILE_BYTES = 25 * 1024 * 1024;

/**
 * Ce se pune în `accept` pe `<input type="file">`.
 *
 * ⚠️ **Extensiile **și** tipurile.** Doar tipurile ar fi ascuns fișierele pe care sistemul
 * de operare le raportează ca `application/octet-stream`, adică exact PDF-urile venite de la
 * unele scannere; doar extensiile ar fi lăsat pe dinafară pozele făcute pe telefon, care pe
 * Android ajung în selector fără nume.
 *
 * ⚠️ **Nu e o verificare.** `accept` filtrează fereastra de alegere și se ocolește cu o
 * tragere cu mouse-ul. Ce contează rămâne pe server, în `isAcceptedDocumentFile`.
 */
export const DOCUMENT_FILE_ACCEPT = [
  ...ACCEPTED_DOCUMENT_EXTENSIONS,
  'application/pdf',
  'image/*',
].join(',');

/**
 * Tipul, normalizat: `application/pdf; charset=binary` și `APPLICATION/PDF` sunt același
 * lucru, iar serverele îl scriu în toate felurile.
 */
export function normalizeMimeType(contentType: string): string {
  return (contentType.split(';')[0] ?? '').trim().toLowerCase();
}

/** Extensia numelui, cu punct, litere mici. Gol dacă n-are. */
export function extensionOf(filename: string): string {
  const dot = filename.lastIndexOf('.');

  return dot === -1 ? '' : filename.slice(dot).toLowerCase();
}

/**
 * E un tip pe care îl primim?
 *
 * `application/octet-stream` **nu** se respinge din start: e ce trimit destule clienți de
 * mail pentru un PDF, și e ce ajunge de la unele scannere și în formularul de încărcare.
 * Pentru el decide extensia — singura informație rămasă.
 */
export function isAcceptedDocumentFile(filename: string, contentType: string): boolean {
  const mime = normalizeMimeType(contentType);

  if (ACCEPTED_DOCUMENT_MIME_TYPES.includes(mime)) {
    return true;
  }

  return (
    (mime === 'application/octet-stream' || mime === '') &&
    ACCEPTED_DOCUMENT_EXTENSIONS.includes(extensionOf(filename))
  );
}

/**
 * Extensia sub care se salvează fișierul, dedusă din tip.
 *
 * ⚠️ **Nu se ia din numele primit.** Numele vine din afară — de la un expeditor de email sau
 * de pe discul operatorului; extensia dedusă din tipul pe care chiar l-am verificat e
 * singura care nu poate ajunge `.php` sau `..%2f`.
 */
export function storageExtension(contentType: string, filename: string): string {
  const mime = normalizeMimeType(contentType);
  const byMime: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/heic': '.heic',
    'image/heif': '.heif',
    'image/webp': '.webp',
    'image/tiff': '.tif',
  };
  const known = byMime[mime];

  if (known !== undefined) {
    return known;
  }

  const extension = extensionOf(filename);

  return ACCEPTED_DOCUMENT_EXTENSIONS.includes(extension) ? extension : '.bin';
}

/**
 * Tipul sub care se păstrează fișierul.
 *
 * Un `application/octet-stream` acceptat pe baza extensiei se salvează cu tipul adevărat,
 * nu cu cel primit: altfel browserul ar descărca PDF-ul în loc să-l arate în previzualizare.
 */
export function storageMimeType(contentType: string, filename: string): string {
  const mime = normalizeMimeType(contentType);

  if (ACCEPTED_DOCUMENT_MIME_TYPES.includes(mime)) {
    return mime;
  }

  const byExtension: Record<string, string> = {
    '.pdf': 'application/pdf',
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.heic': 'image/heic',
    '.heif': 'image/heif',
    '.webp': 'image/webp',
    '.tif': 'image/tiff',
    '.tiff': 'image/tiff',
  };

  return byExtension[extensionOf(filename)] ?? 'application/octet-stream';
}
